// Live camera barcode scanner. @zxing/browser is dynamically imported so it
// stays in its own chunk, off the main bundle, loaded only when the scanner opens.
import { useEffect, useRef, useState } from "preact/hooks";
import type { IScannerControls } from "@zxing/browser";

export function BarcodeScanner({
  onDetected,
  onClose,
}: {
  onDetected: (code: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let controls: IScannerControls | null = null;
    let cancelled = false;

    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        if (!videoRef.current || cancelled) return;
        controls = await reader.decodeFromVideoDevice(
          undefined, // default = rear camera on phones
          videoRef.current,
          (result) => {
            if (result && !cancelled) {
              cancelled = true;
              controls?.stop();
              onDetected(result.getText());
            }
          },
        );
      } catch (e) {
        setErr(
          e instanceof Error && e.name === "NotAllowedError"
            ? "Camera permission denied. Enter the barcode number instead."
            : "Couldn't start the camera. Enter the barcode number instead.",
        );
      }
    })();

    return () => {
      cancelled = true;
      controls?.stop();
    };
  }, [onDetected]);

  return (
    <div class="scanner fade-in">
      <video ref={videoRef} class="scanner-video" playsinline muted />
      <div class="scanner-frame" />
      <p class="scanner-hint">{err || "Point the camera at a barcode"}</p>
      <button class="btn secondary" onClick={onClose}>Cancel</button>
    </div>
  );
}
